import { Frame } from '../atoms/Frame';
import { Clock } from '../atoms/Icons';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { P, S, T, G } from '../../../styles/tokens';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

interface TemplateProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  onRetry: () => void;
  onLeaveHome?: () => void;
}

interface RecoverableCopy {
  title: string;
  body: string;
  cta: string;
}

function firstValidatorExtractedData(attempt?: CheckpointAttempt): Record<string, unknown> {
  const validators = attempt?.verdict_payload?.validators;
  if (!Array.isArray(validators)) return {};
  for (const item of validators) {
    if (!item || typeof item !== 'object') continue;
    const extracted = (item as Record<string, unknown>).extracted_data;
    if (extracted && typeof extracted === 'object') {
      return extracted as Record<string, unknown>;
    }
  }
  return {};
}

function recoverableCopy(attempt?: CheckpointAttempt): RecoverableCopy {
  const payload = attempt?.verdict_payload ?? {};
  const extracted = firstValidatorExtractedData(attempt);
  const escalationClass = String(payload.escalation_class ?? extracted.escalation_class ?? '');
  const reason = String(extracted.reason ?? payload.reason ?? '');
  const recoveryAction = String(extracted.recovery_action ?? payload.recovery_action ?? '');

  if (
    reason === 'marketplace_review_pending' ||
    recoveryAction === 'wait_for_final_marketplace_status'
  ) {
    return {
      title: 'Маркетплейс ещё проверяет отзыв',
      body: 'Дождитесь финального статуса: опубликован или отклонён. После этого отправьте новый скрин.',
      cta: 'Отправить новый скрин',
    };
  }

  if (escalationClass === 'infra') {
    return {
      title: 'Проверка не сработала',
      body: 'Это технический сбой на нашей стороне. Попробуйте отправить скрин ещё раз — попытка не сгорела.',
      cta: 'Отправить ещё раз',
    };
  }

  return {
    title: 'Нужен новый скрин',
    body: 'Мы не смогли уверенно проверить этот шаг. Отправьте proof ещё раз — попытка не сгорела.',
    cta: 'Отправить ещё раз',
  };
}

export function RecoverableEscalationStatus({ attempt, onRetry, onLeaveHome }: TemplateProps) {
  const copy = recoverableCopy(attempt);

  return (
    <Frame>
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0,
          padding: '0 24px',
          textAlign: 'center',
        }}
      >
        {attempt?.artefact_url && /^https?:\/\//.test(attempt.artefact_url) && (
          <div
            style={{
              width: 220,
              height: 280,
              borderRadius: 16,
              overflow: 'hidden',
              background: '#0E0A06',
              position: 'relative',
              boxShadow: '0 12px 30px rgba(20,12,8,0.18)',
              marginBottom: 24,
            }}
          >
            <img
              src={attempt.artefact_url}
              alt="Загруженное фото"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        )}

        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: P.warn,
            marginBottom: 16,
            ...G.warmTag,
          }}
        >
          <Clock size={28} />
        </div>

        <div
          style={{
            font: T.headline,
            color: S.label,
            marginBottom: 6,
          }}
        >
          {copy.title}
        </div>

        <div
          style={{
            font: T.footnote,
            color: S.secondaryLabel,
            lineHeight: 1.5,
            maxWidth: 280,
            marginBottom: 16,
          }}
        >
          {copy.body}
        </div>

        {onLeaveHome && (
          <button
            type="button"
            onClick={onLeaveHome}
            style={{
              border: 'none',
              background: 'transparent',
              color: P.muted,
              font: T.footnote,
              textDecoration: 'underline',
              padding: '8px 0',
              cursor: 'pointer',
            }}
          >
            Вернуться на главную
          </button>
        )}
      </div>

      <WizardCtaBar label={copy.cta} onClick={onRetry} />
    </Frame>
  );
}
